import { ethers } from "ethers";
import sample from "lodash/sample";

import {
  ARBITRUM,
  AVALANCHE,
  AVALANCHE_FUJI,
  BOTANIX,
  SUPPORTED_CHAIN_IDS as SDK_SUPPORTED_CHAIN_IDS,
  SUPPORTED_CHAIN_IDS_DEV as SDK_SUPPORTED_CHAIN_IDS_DEV,
  UiSupportedChain,
  UiContractsChain,
  getChainName,
  CHAIN_NAMES_MAP,
} from "sdk/configs/chains";

import { isDevelopment } from "./env";
import { ETH_MAINNET, ASSETCHAIN, ASSETCHAIN_TESTNET } from "./static/chains";

export * from "./static/chains";
export { getChainName, CHAIN_NAMES_MAP, ARBITRUM, AVALANCHE };
export type { UiSupportedChain, UiContractsChain };

export const SUPPORTED_CHAIN_IDS = isDevelopment() ? SDK_SUPPORTED_CHAIN_IDS_DEV : SDK_SUPPORTED_CHAIN_IDS;

const { parseEther } = ethers;

export const ENV_BOTANIX_RPC_URLS = import.meta.env.VITE_APP_BOTANIX_RPC_URLS;
export const ENV_ASSETCHAIN_RPC_URLS = import.meta.env.VITE_APP_ASSETCHAIN_RPC_URLS;

// TODO take it from web3
const ENV_DEFAULT_CHAIN_ID = Number(import.meta.env.VITE_DEFAULT_CHAIN_ID);
export const DEFAULT_CHAIN_ID = SUPPORTED_CHAIN_IDS.includes(ENV_DEFAULT_CHAIN_ID as UiSupportedChain)
  ? (ENV_DEFAULT_CHAIN_ID as UiSupportedChain)
  : ASSETCHAIN_TESTNET;
export const CHAIN_ID = DEFAULT_CHAIN_ID;

export const IS_NETWORK_DISABLED: Record<UiSupportedChain, boolean> = {
  [BOTANIX]: true,
  [ASSETCHAIN]: false,
  [ASSETCHAIN_TESTNET]: false,
};

export const NETWORK_EXECUTION_TO_CREATE_FEE_FACTOR = {
  [ASSETCHAIN]: 10n ** 29n * 5n,
  [ASSETCHAIN_TESTNET]: 10n ** 29n * 2n,
} as const;

const constants = {
  [BOTANIX]: {
    nativeTokenSymbol: "BTC",
    wrappedTokenSymbol: "PBTC",
    defaultCollateralSymbol: "USDC.E",
    defaultFlagOrdersEnabled: true,
    positionReaderPropsLength: 9,
    v2: true,

    SWAP_ORDER_EXECUTION_GAS_FEE: parseEther("0.01"),
    INCREASE_ORDER_EXECUTION_GAS_FEE: parseEther("0.01"),
    // contract requires that execution fee be strictly greater than instead of gte
    DECREASE_ORDER_EXECUTION_GAS_FEE: parseEther("0.0100001"),
  },

  [ASSETCHAIN]: {
    nativeTokenSymbol: "RWA",
    wrappedTokenSymbol: "WRWA",
    defaultCollateralSymbol: "USDC",
    defaultFlagOrdersEnabled: true,
    positionReaderPropsLength: 9,
    v2: true,

    SWAP_ORDER_EXECUTION_GAS_FEE: parseEther("0.02"),
    INCREASE_ORDER_EXECUTION_GAS_FEE: parseEther("0.02"),
    // contract requires that execution fee be strictly greater than instead of gte
    DECREASE_ORDER_EXECUTION_GAS_FEE: parseEther("0.0200001"),
  },

  [ASSETCHAIN_TESTNET]: {
    nativeTokenSymbol: "RWA",
    wrappedTokenSymbol: "WRWA",
    defaultCollateralSymbol: "USDC",
    defaultFlagOrdersEnabled: true,
    positionReaderPropsLength: 9,
    v2: true,

    SWAP_ORDER_EXECUTION_GAS_FEE: parseEther("0.02"),
    INCREASE_ORDER_EXECUTION_GAS_FEE: parseEther("0.02"),
    // contract requires that execution fee be strictly greater than instead of gte
    DECREASE_ORDER_EXECUTION_GAS_FEE: parseEther("0.0200001"),
  },
};
const LEGACY_CONSTANTS = {
  [ARBITRUM]: {
    nativeTokenSymbol: "ETH",
    wrappedTokenSymbol: "WETH",
    defaultCollateralSymbol: "USDC",
    defaultFlagOrdersEnabled: true,
    positionReaderPropsLength: 9,
    v2: true,
    SWAP_ORDER_EXECUTION_GAS_FEE: parseEther("0.000025"),
    INCREASE_ORDER_EXECUTION_GAS_FEE: parseEther("0.000025"),
    DECREASE_ORDER_EXECUTION_GAS_FEE: parseEther("0.000026"),
  },
  [AVALANCHE]: {
    nativeTokenSymbol: "AVAX",
    wrappedTokenSymbol: "WAVAX",
    defaultCollateralSymbol: "USDC.e",
    defaultFlagOrdersEnabled: true,
    positionReaderPropsLength: 9,
    v2: true,
    SWAP_ORDER_EXECUTION_GAS_FEE: parseEther("0.002"),
    INCREASE_ORDER_EXECUTION_GAS_FEE: parseEther("0.002"),
    DECREASE_ORDER_EXECUTION_GAS_FEE: parseEther("0.0021"),
  },
  [AVALANCHE_FUJI]: {
    nativeTokenSymbol: "AVAX",
    wrappedTokenSymbol: "WAVAX",
    defaultCollateralSymbol: "USDC.e",
    defaultFlagOrdersEnabled: true,
    positionReaderPropsLength: 9,
    v2: true,
    SWAP_ORDER_EXECUTION_GAS_FEE: parseEther("0.002"),
    INCREASE_ORDER_EXECUTION_GAS_FEE: parseEther("0.002"),
    DECREASE_ORDER_EXECUTION_GAS_FEE: parseEther("0.0021"),
  },
};
Object.assign(constants as Record<number, (typeof constants)[typeof BOTANIX]>, LEGACY_CONSTANTS);

const ALCHEMY_WHITELISTED_DOMAINS = ["gmx.io", "app.gmx.io"];

export const RPC_PROVIDERS: Record<number, string[]> = {
  [ETH_MAINNET]: ["https://rpc.ankr.com/eth"],
  [ARBITRUM]: ["https://arb1.arbitrum.io/rpc"],
  [AVALANCHE]: ["https://api.avax.network/ext/bc/C/rpc"],
  [AVALANCHE_FUJI]: ["https://api.avax-test.network/ext/bc/C/rpc"],
  [BOTANIX]: [
    // returns incorrect gas price
    // "https://rpc.botanixlabs.com",
    "https://rpc.ankr.com/botanix_mainnet",
  ],
  [ASSETCHAIN]: [
    "https://mainnet-rpc.assetchain.org",
  ],
  [ASSETCHAIN_TESTNET]: ["https://enugu-rpc.assetchain.org"],
};

export const FALLBACK_PROVIDERS: Record<number, string[]> = {
  [ARBITRUM]: ["https://arb1.arbitrum.io/rpc"],
  [AVALANCHE]: ["https://api.avax.network/ext/bc/C/rpc"],
  [AVALANCHE_FUJI]: ["https://api.avax-test.network/ext/bc/C/rpc"],
  [BOTANIX]: ENV_BOTANIX_RPC_URLS ? JSON.parse(ENV_BOTANIX_RPC_URLS) : [getAlchemyBotanixHttpUrl()],
  [ASSETCHAIN]: ENV_ASSETCHAIN_RPC_URLS ? JSON.parse(ENV_ASSETCHAIN_RPC_URLS) : ["https://mainnet-rpc.assetchain.org"],
  [ASSETCHAIN_TESTNET]: ["https://enugu-rpc.assetchain.org"],
};

export const getConstant = (chainId: number, key: string) => {
  if (!constants[chainId]) {
    throw new Error(`Unsupported chainId ${chainId}`);
  }

  if (!(key in constants[chainId])) {
    throw new Error(`Key ${key} does not exist for chainId ${chainId}`);
  }

  return constants[chainId][key];
};

export function getFallbackRpcUrl(chainId: number): string {
  return sample(FALLBACK_PROVIDERS[chainId]) || RPC_PROVIDERS[chainId]?.[0] || "";
}

function getAlchemyKey() {
  if (ALCHEMY_WHITELISTED_DOMAINS.includes(self.location.host)) {
    return "RcaXYTizJs51m-w9SnRyDrxSZhE5H9Mf";
  }
  return "EmVYwUw0N2tXOuG0SZfe5Z04rzBsCbr2";
}

export function getAlchemyBotanixHttpUrl() {
  return `https://botanix-mainnet.g.alchemy.com/v2/${getAlchemyKey()}`;
}

export function getAlchemyBotanixWsUrl() {
  return `wss://botanix-mainnet.g.alchemy.com/v2/${getAlchemyKey()}`;
}

export function getExplorerUrl(chainId) {
  if (chainId === 3) {
    return "https://ropsten.etherscan.io/";
  } else if (chainId === 42) {
    return "https://kovan.etherscan.io/";
  } else if (chainId === ARBITRUM) {
    return "https://arbiscan.io/";
  } else if (chainId === AVALANCHE) {
    return "https://snowtrace.io/";
  } else if (chainId === AVALANCHE_FUJI) {
    return "https://testnet.snowtrace.io/";
  } else if (chainId === BOTANIX) {
    return "https://botanixscan.io/";
  } else if (chainId === ASSETCHAIN) {
    return "https://scan.assetchain.org/";
  } else if (chainId === ASSETCHAIN_TESTNET) {
    return "https://scan-testnet.assetchain.org/";
  }
  return "https://etherscan.io/";
}

export function getTokenExplorerUrl(chainId: number, tokenAddress: string) {
  return `${getExplorerUrl(chainId)}token/${tokenAddress}`;
}
